import React, { useRef, useEffect } from 'react';
import { ReceiptData } from '../../utils/printReceipt';

interface ReceiptModalProps {
  receipt: ReceiptData;
  isOpen: boolean;
  onClose: () => void;
}

const ReceiptModal: React.FC<ReceiptModalProps> = ({ receipt, isOpen, onClose }) => {
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Focus trap - prevent background scrolling
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handlePrint = () => {
    if (printRef.current) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        const html = generatePrintHTML();
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
        
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => printWindow.close();
        }, 250);
      } else {
        // Fallback: Use browser print
        window.print();
      }
    }
  };

  const handleDownload = () => {
    const html = generatePrintHTML();
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `receipt_${receipt.invoice_no}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generatePrintHTML = (): string => {
    const width = receipt.printer_settings?.paper_width || 80;
    const fontSize = receipt.printer_settings?.font_size || 12;

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Receipt - ${receipt.invoice_no}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Courier New', monospace;
      font-size: ${fontSize}px;
      width: ${width}mm;
      margin: 0 auto;
      padding: 5mm;
      background: white;
    }
    @media print {
      body { padding: 0; }
      @page { margin: 0; size: ${width}mm auto; }
    }
    .center { text-align: center; }
    .right { text-align: right; }
    .bold { font-weight: bold; }
    .line { border-top: 1px dashed #000; margin: 5px 0; }
    table { width: 100%; border-collapse: collapse; margin: 5px 0; }
    td { padding: 2px 0; vertical-align: top; }
    h2 { font-size: ${fontSize + 4}px; margin: 5px 0; }
  </style>
</head>
<body>
  <div class="center">
    <h2>${(receipt.branch_name || 'PhoneLabs POS').toUpperCase()}</h2>
    ${receipt.branch_address ? `<div>${receipt.branch_address}</div>` : ''}
    ${receipt.branch_phone ? `<div>Telp: ${receipt.branch_phone}</div>` : ''}
  </div>
  
  <div class="line"></div>
  
  <table>
    <tr><td>No Invoice</td><td class="right bold">${receipt.invoice_no}</td></tr>
    <tr><td>Tanggal</td><td class="right">${formatDate(receipt.sale_date)}</td></tr>
    <tr><td>Kasir</td><td class="right">${receipt.cashier_name}</td></tr>
    ${receipt.customer_name ? `<tr><td>Customer</td><td class="right">${receipt.customer_name}</td></tr>` : ''}
  </table>
  
  <div class="line"></div>
  
  <table>
    ${receipt.items.map(item => `
      <tr>
        <td colspan="2" class="bold">${item.name}</td>
      </tr>
      <tr>
        <td>${item.qty} x ${formatCurrency(item.price)}</td>
        <td class="right">${formatCurrency(item.qty * item.price)}</td>
      </tr>
      ${item.discount > 0 ? `<tr><td>  Diskon</td><td class="right">-${formatCurrency(item.discount)}</td></tr>` : ''}
    `).join('')}
  </table>
  
  <div class="line"></div>
  
  <table>
    <tr><td>Subtotal</td><td class="right">${formatCurrency(receipt.subtotal)}</td></tr>
    ${receipt.discount > 0 ? `<tr><td>Diskon</td><td class="right">-${formatCurrency(receipt.discount)}</td></tr>` : ''}
    <tr><td class="bold">TOTAL</td><td class="right bold">${formatCurrency(receipt.total)}</td></tr>
  </table>
  
  <div class="line"></div>
  
  <table>
    ${receipt.payment_details && receipt.payment_details.length > 0 ? receipt.payment_details.map(payment => `
      <tr><td>${payment.method}</td><td class="right">${formatCurrency(payment.amount)}</td></tr>
    `).join('') : ''}
    <tr><td>Bayar</td><td class="right">${formatCurrency(receipt.amount_paid)}</td></tr>
    ${receipt.change > 0 ? `<tr><td>Kembali</td><td class="right bold">${formatCurrency(receipt.change)}</td></tr>` : ''}
  </table>
  
  ${receipt.notes ? `
    <div class="line"></div>
    <div><div class="bold">Catatan:</div><div>${receipt.notes}</div></div>
  ` : ''}
  
  <div class="line"></div>
  
  <div class="center">
    <div>Terima kasih atas kunjungan Anda!</div>
    <div>*** STRUK INI ADALAH BUKTI PEMBAYARAN SAH ***</div>
  </div>
</body>
</html>`;
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-600 to-yellow-700 text-white px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">Nota Transaksi</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Receipt Content */}
        <div 
          ref={printRef}
          className="flex-1 overflow-y-auto p-6 bg-gray-50"
          style={{ fontFamily: "'Courier New', monospace" }}
        >
          {/* Branch Info */}
          <div className="text-center mb-4 pb-4 border-b-2 border-dashed border-gray-300">
            <h3 className="text-lg font-bold uppercase">{receipt.branch_name || 'PhoneLabs POS'}</h3>
            {receipt.branch_address && <div className="text-sm">{receipt.branch_address}</div>}
            {receipt.branch_phone && <div className="text-sm">Telp: {receipt.branch_phone}</div>}
          </div>

          {/* Transaction Info */}
          <div className="mb-4 pb-4 border-b-2 border-dashed border-gray-300 text-sm">
            <div className="flex justify-between mb-1">
              <span>No Invoice</span>
              <span className="font-bold">{receipt.invoice_no}</span>
            </div>
            <div className="flex justify-between mb-1">
              <span>Tanggal</span>
              <span>{formatDate(receipt.sale_date)}</span>
            </div>
            <div className="flex justify-between mb-1">
              <span>Kasir</span>
              <span>{receipt.cashier_name}</span>
            </div>
            {receipt.customer_name && (
              <div className="flex justify-between">
                <span>Customer</span>
                <span>{receipt.customer_name}</span>
              </div>
            )}
          </div>

          {/* Items */}
          <div className="mb-4 pb-4 border-b-2 border-dashed border-gray-300 text-sm">
            {receipt.items.map((item, index) => (
              <div key={index} className="mb-3">
                <div className="font-bold">{item.name}</div>
                <div className="flex justify-between">
                  <span>{item.qty} x {formatCurrency(item.price)}</span>
                  <span>{formatCurrency(item.qty * item.price)}</span>
                </div>
                {item.discount > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span className="pl-4">Diskon</span>
                    <span>-{formatCurrency(item.discount)}</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="mb-4 pb-4 border-b-2 border-dashed border-gray-300 text-sm">
            <div className="flex justify-between mb-1">
              <span>Subtotal</span>
              <span>{formatCurrency(receipt.subtotal)}</span>
            </div>
            {receipt.discount > 0 && (
              <div className="flex justify-between mb-1 text-red-600">
                <span>Diskon</span>
                <span>-{formatCurrency(receipt.discount)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-base mt-2">
              <span>TOTAL</span>
              <span>{formatCurrency(receipt.total)}</span>
            </div>
          </div>

          {/* Payment */}
          <div className="mb-4 pb-4 border-b-2 border-dashed border-gray-300 text-sm">
            {receipt.payment_details && receipt.payment_details.length > 0 && (
              receipt.payment_details.map((payment, index) => (
                <div key={index} className="flex justify-between mb-1">
                  <span>{payment.method}</span>
                  <span>{formatCurrency(payment.amount)}</span>
                </div>
              ))
            )}
            <div className="flex justify-between mb-1">
              <span>Bayar</span>
              <span>{formatCurrency(receipt.amount_paid)}</span>
            </div>
            {receipt.change > 0 && (
              <div className="flex justify-between font-bold">
                <span>Kembali</span>
                <span>{formatCurrency(receipt.change)}</span>
              </div>
            )}
          </div>

          {/* Notes */}
          {receipt.notes && (
            <div className="mb-4 pb-4 border-b-2 border-dashed border-gray-300 text-sm">
              <div className="font-bold mb-1">Catatan:</div>
              <div>{receipt.notes}</div>
            </div>
          )}

          {/* Footer */}
          <div className="text-center text-sm">
            <div className="mb-1">Terima kasih atas kunjungan Anda!</div>
            <div className="font-bold">*** STRUK INI ADALAH BUKTI PEMBAYARAN SAH ***</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="border-t border-gray-200 px-6 py-4 flex gap-3">
          <button
            onClick={handlePrint}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print
          </button>
          <button
            onClick={handleDownload}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download
          </button>
          <button
            onClick={onClose}
            className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
          >
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;
